export type EnquiryRole = 'learner' | 'recruiter'

export type EnquiryField =
    | 'role'
    | 'fullName'
    | 'phone'
    | 'email'
    | 'city'
    | 'course'
    | 'education'
    | 'company'
    | 'rolesNeeded'
    | 'openings'
    | 'message'

export type EnquiryInput = Record<EnquiryField, string>

export type EnquiryErrors = Partial<Record<EnquiryField, string>>

export type EnquiryData = {
    role: EnquiryRole
    fullName: string
    phone: string
    email: string | null
    city: string
    course: string | null
    education: string | null
    company: string | null
    rolesNeeded: string | null
    openings: string | null
    message: string | null
}

export const COURSE_OPTIONS: string[] = ["Auto Aspire", "BFSI Aspire", "Customer Relationship", "Not sure yet"]

export const EDUCATION_OPTIONS: string[] = ["School dropout", "Diploma", "Undergraduate", "Graduate", "Postgraduate", "Professional degree"]

export const OPENINGS_OPTIONS: string[] = ["1 to 10", "11 to 50", "51 to 200", "More than 200"]

export const LIMITS = {
    fullName: 80,
    email: 120,
    city: 60,
    company: 120,
    rolesNeeded: 160,
    message: 1000
}

export const ENQUIRY_ROLE_EVENT = 'skillkafe:enquiry-role'

// Lets any call-to-action preselect the role in the callback form before it scrolls to it
export const selectEnquiryRole = (role: EnquiryRole): void => {
    if (typeof window === 'undefined') return

    window.dispatchEvent(new CustomEvent<EnquiryRole>(ENQUIRY_ROLE_EVENT, { detail: role }))
}

export const emptyEnquiry = (role: EnquiryRole = 'learner'): EnquiryInput => ({
    role,
    fullName: '',
    phone: '',
    email: '',
    city: '',
    course: '',
    education: '',
    company: '',
    rolesNeeded: '',
    openings: '',
    message: ''
})

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const singleLine = (value: unknown): string => {
    return typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : ''
}

const multiLine = (value: unknown): string => {
    return typeof value === 'string' ? value.replace(/\r\n/g, '\n').trim() : ''
}

// Accepts Indian mobile numbers with or without +91 or 0, plus international numbers that start with +
export const normalizePhone = (value: string): string | null => {
    const trimmed = value.trim()
    const digits = trimmed.replace(/\D/g, '')

    if (/^[6-9]\d{9}$/.test(digits)) return `+91${digits}`
    if (/^0[6-9]\d{9}$/.test(digits)) return `+91${digits.slice(1)}`
    if (/^91[6-9]\d{9}$/.test(digits)) return `+${digits}`
    if (trimmed.startsWith('+') && digits.length >= 8 && digits.length <= 15) return `+${digits}`

    return null
}

export const validateEnquiry = (input: Partial<Record<EnquiryField, unknown>>): { data: EnquiryData | null, errors: EnquiryErrors } => {
    const errors: EnquiryErrors = {}

    const role: EnquiryRole | null = input.role === 'recruiter' ? 'recruiter' : input.role === 'learner' ? 'learner' : null

    if (!role) errors.role = "Choose whether you want to learn or to hire."

    const fullName = singleLine(input.fullName)

    if (fullName.length < 2) errors.fullName = "Enter your full name."
    else if (fullName.length > LIMITS.fullName) errors.fullName = `Keep your name under ${LIMITS.fullName} characters.`

    const phoneValue = singleLine(input.phone)
    const phone = normalizePhone(phoneValue)

    if (!phoneValue) errors.phone = "Enter your phone number."
    else if (!phone) errors.phone = "Enter a valid 10-digit mobile number."

    const email = singleLine(input.email).toLowerCase()

    if (email && (!EMAIL_PATTERN.test(email) || email.length > LIMITS.email)) errors.email = "Enter a valid email address."
    else if (!email && role === 'recruiter') errors.email = "Enter your work email so we can reply."

    const city = singleLine(input.city)

    if (city.length < 2) errors.city = role === 'recruiter' ? "Enter the city you are hiring in." : "Enter your city."
    else if (city.length > LIMITS.city) errors.city = `Keep the city under ${LIMITS.city} characters.`

    let course: string | null = null
    let education: string | null = null
    let company: string | null = null
    let rolesNeeded: string | null = null
    let openings: string | null = null

    if (role === 'learner') {
        const courseValue = singleLine(input.course)
        const educationValue = singleLine(input.education)

        if (COURSE_OPTIONS.includes(courseValue)) course = courseValue
        else errors.course = "Choose the course you are interested in."

        if (educationValue) {
            if (EDUCATION_OPTIONS.includes(educationValue)) education = educationValue
            else errors.education = "Choose your education level."
        }
    }

    if (role === 'recruiter') {
        const companyValue = singleLine(input.company)
        const rolesValue = singleLine(input.rolesNeeded)
        const openingsValue = singleLine(input.openings)

        if (companyValue.length < 2) errors.company = "Enter your company name."
        else if (companyValue.length > LIMITS.company) errors.company = `Keep the company name under ${LIMITS.company} characters.`
        else company = companyValue

        if (rolesValue.length > LIMITS.rolesNeeded) errors.rolesNeeded = `Keep this under ${LIMITS.rolesNeeded} characters.`
        else rolesNeeded = rolesValue || null

        if (OPENINGS_OPTIONS.includes(openingsValue)) openings = openingsValue
        else errors.openings = "Choose how many people you want to hire."
    }

    const message = multiLine(input.message)

    if (message.length > LIMITS.message) errors.message = `Keep your message under ${LIMITS.message} characters.`

    if (!role || !phone || Object.keys(errors).length > 0) return { data: null, errors }

    return {
        data: {
            role,
            fullName,
            phone,
            email: email || null,
            city,
            course,
            education,
            company,
            rolesNeeded,
            openings,
            message: message || null
        },
        errors
    }
}
